import { Pie } from '@ant-design/charts';
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Form, Image, message, Progress, Tooltip, UploadFile } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { isDefined } from 'class-validator';
import DOMPurify from 'dompurify';
import html2pdf from 'html2pdf.js';
import { t } from 'i18next';
import fileDownload from 'js-file-download';
import JSZip from 'jszip';
import React, {
  CSSProperties,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AiFillLike, AiFillMinusCircle, AiOutlineLike, AiOutlineMinusCircle } from 'react-icons/ai';
import { BiDislike, BiSolidDislike } from 'react-icons/bi';
import { FaChartBar } from 'react-icons/fa';
import { FiEye } from 'react-icons/fi';
import { MdCancel, MdCheckCircle, MdCheckCircleOutline, MdOutlineCancel } from 'react-icons/md';
import { PiDownloadSimple } from 'react-icons/pi';
import ReactPlayer from 'react-player';
import { Rnd } from 'react-rnd';
import BlockStatisticList from 'src/app/components/BlockStatisticComponent/BlockStatisticList';
import DraggableModal from 'src/app/components/DraggableModalComponent/DraggableModal';
import EllipsisTooltip from 'src/app/components/EllipsisTooltipComponent/EllipsisTooltip';
import styles from 'src/app/components/NewsInfoComponent/NewsInfo.module.scss';
import PDFViewer from 'src/app/components/PDFViewer/PDFViewer';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import { BRONZE_ACCOUNT_ID } from 'src/app/constants/constants';
import PersonIdentifiedForm from 'src/app/forms/PersonIdentifiedForm/PersonIdentifiedForm';
import { formHelper, timeHelper } from 'src/app/helpers';
import { getEnumKeyByValue } from 'src/app/helpers/enum.helper';
import { base64ToUploadFile } from 'src/app/helpers/image.helper';
import { getMIMEType } from 'src/app/helpers/parser.helper';
import { genericExcludeFilter } from 'src/app/helpers/permission.helper';
import { SidebarContext } from 'src/app/layouts/default/LayoutDefault';
import { mediaTypeConf } from 'src/app/pages/InterestEditPage/chain-view/media-type-style';
import { convertInterestListToCategories } from 'src/app/pages/InterestEditPage/interest-edit.config';
import { EditBlockDataType } from 'src/app/redux/block/block-slice.type';
import { useEditBlockMutation } from 'src/app/redux/block/blockSlice';
import {
  useCreateBlockStatisticMutation,
  useGetBlockStatisticsQuery,
  useUpdateBlockStatisticMutation,
} from 'src/app/redux/blockStatistic/blockStatisticSlice';
import { useAppSelector } from 'src/app/redux/hook';
import { useGetAllInterestsQuery } from 'src/app/redux/interests/interestsSlice';
import { multipleConfig } from 'src/app/services/control-panel/control-panel.service';
import { BatP, FenceNode, Keypad, SeNot, UrlInfoResultType } from 'src/app/services/dfm-service/dfm.service.type';
import { BlockResultType } from 'src/app/services/surfer-service/surfer.service.type';
import { dfmAxios } from 'src/app/utils/axios.instance';
import { FenceBatteryStatus, FencePowerSource, FenceSensorStatus } from 'src/enums/nginr-type.enum';
import { BlockStatisticRelevantStatus, BlockStatisticStatus, MediaType } from 'src/enums/surfer.enums';
import { EditIcon } from 'src/icons';
import AddIcon from 'src/icons/AddIcon';
import NoDataIcon from 'src/icons/NoDataIcon';

const TEXT_MIME_TYPES = [
  'text/plain',
  'text/html',
  'text/css',
  'text/javascript',
  'application/json',
  'application/xml',
  'application/xhtml+xml',
  'application/javascript',
  'message/rfc822',
];

function ResizeDot() {
  return (
    <div
      style={{
        pointerEvents: 'auto',
        width: 14,
        height: 14,
        borderRadius: 999,
        background: '#fff',
        border: '2px solid #ef4444',
        boxShadow: '0 4px 14px rgba(0,0,0,.18)',
        marginLeft: '.15rem',
        marginTop: '.15rem',
      }}
    />
  );
}

// This component is responsible for displaying image for the block that has image file
const BlockImage = ({ fileUrl, text }: { fileUrl?: string; text?: string }) => {
  const [imageLoaded, setImageLoaded] = useState<boolean>(true);
  const { theme } = useAppSelector((state) => state.configContent);
  const [imgUrl, setImgUrl] = useState<string>();

  const sendRequest = async () => {
    const { data }: any = await multipleConfig({ moduleName: 'nginrDFM', portType: 2 });
    const urlWithDfm = `https://${data.moduleHost}/dfm/${fileUrl}`;
    setImgUrl(urlWithDfm);
  };

  useEffect(() => {
    sendRequest();
  }, [fileUrl]);

  if (!imgUrl) return null;
  return (
    <div className={styles.imgWrapper}>
      {imageLoaded ? (
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '1rem',
            color: 'var(--primary_text_color)',
          }}
        >
          <Image
            style={{ height: '57vh' }}
            wrapperStyle={{ height: '100%' }}
            src={imgUrl}
            onError={() => setImageLoaded(false)}
          />
          {text}
        </div>
      ) : (
        <NoDataIcon color={theme.colors.quoteBgc} />
      )}
    </div>
  );
};

const DEFAULT_SCREENSHOTS = [
  { name: 'empty', uid: '0' },
  { name: 'empty', uid: '1' },
  { name: 'empty', uid: '2' },
  { name: 'empty', uid: '3' },
  { name: 'empty', uid: '4' },
];
const BlockVideo = ({ file, blockId }: { file: string | undefined; blockId: string }) => {
  const playerRef = useRef<any>(null);
  const rndRef = useRef<any>(null);
  const [screenShots, setScreenShots] = useState<UploadFile[]>(DEFAULT_SCREENSHOTS);
  const [personForm] = Form.useForm();
  const [learnBtnActive, setLearnBtnActive] = useState(false);
  const [videoPaused, setVideoPaused] = useState(false);
  const { theme } = useAppSelector((state) => state.configContent);
  const [videoUrl, setVideoUrl] = useState<string>();
  const [progress, setProgress] = useState(0);

  const clamp = (val: number, min: number, max: number) => {
    return Math.max(min, Math.min(val, max));
  };

  const calcContainBox = (containerEl: any, videoEl: any) => {
    const r = containerEl.getBoundingClientRect();
    const vw = videoEl.videoWidth;
    const vh = videoEl.videoHeight;

    if (!r.width || !r.height) return { x: 0, y: 0, w: 0, h: 0 };
    if (!vw || !vh) return { x: 0, y: 0, w: r.width, h: r.height };

    const scale = Math.min(r.width / vw, r.height / vh);
    const w = vw * scale;
    const h = vh * scale;
    const x = (r.width - w) / 2;
    const y = (r.height - h) / 2;
    return { x, y, w, h };
  };

  const handleCapture = async (idx: number) => {
    const v = playerRef.current.getInternalPlayer();
    const c = containerRef.current;
    if (!v || !c || !v.videoWidth || !v.videoHeight) return;

    try {
      const b = calcContainBox(c, v);
      const scaleX = v.videoWidth / b.w;
      const scaleY = v.videoHeight / b.h;

      const sx = crop.x * scaleX;
      const sy = crop.y * scaleY;
      const sW = crop.width * scaleX;
      const sH = crop.height * scaleY;

      const dpr = window.devicePixelRatio || 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(crop.width * dpr));
      canvas.height = Math.max(1, Math.round(crop.height * dpr));

      const ctx = canvas.getContext('2d');
      ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);

      ctx?.drawImage(v, sx, sy, sW, sH, 0, 0, crop.width, crop.height);
      const dataURL = canvas.toDataURL('image/png');
      const uploadFile = await base64ToUploadFile(dataURL, `Screenshot${idx + 1}`);
      const ssList = [...screenShots];
      // const firstEmpty = ssList.findIndex((obj) => !obj.url);
      ssList[idx] = uploadFile;
      setScreenShots(ssList);
    } catch (error) {
      console.log('error', error);
    }
  };

  const onAddPerson = () => {
    personForm.setFieldsValue({ pictures: screenShots });
    const modal = showModal({
      title: 'Add Person',
      content: <PersonIdentifiedForm form={personForm} destroyModal={() => modal.destroy()} learn />,
      size: 'medium-2',
      onSave: () => formHelper.submitForm(personForm),
    });
  };

  const sendRequest = async () => {
    const { data }: any = await multipleConfig({ moduleName: 'nginrDFM', portType: 2 });
    const urlWithDfm = `https://${data.moduleHost}/dfm/${file}`;
    const { data: videoData } = await dfmAxios.get(urlWithDfm, {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        const total = progressEvent.total || 1;
        const percentCompleted = Math.round((progressEvent.loaded / total) * 100);
        setProgress(percentCompleted);
      },
    });
    setVideoUrl(URL.createObjectURL(videoData));
  };

  useEffect(() => {
    sendRequest();
  }, [file]);

  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
  }, [videoUrl]);

  const containerRef = useRef(null);

  const [contentBox, setContentBox] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [crop, setCrop] = useState({ x: 0, y: 0, width: 100, height: 100 });

  const recompute = useCallback(() => {
    const c = containerRef.current;
    const v = playerRef.current.getInternalPlayer();

    if (!c || !v) return;
    const b = calcContainBox(c, v);
    setContentBox(b);

    setCrop((cur) => {
      const w = clamp(cur.width, 10, Math.max(10, b.w));
      const h = clamp(cur.height, 10, Math.max(10, b.h));
      const x = clamp(cur.x, 0, Math.max(0, b.w - w));
      const y = clamp(cur.y, 0, Math.max(0, b.h - h));
      if (!b.w || !b.h) return { ...cur, x: 0, y: 0, width: w, height: h };
      return { x, y, width: w, height: h };
    });
  }, []);

  useLayoutEffect(() => {
    const onResize = () => recompute();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recompute]);

  const handleReady = useCallback(() => {
    const internal = playerRef.current.getInternalPlayer();
    if (internal && internal instanceof HTMLVideoElement) {
      const onMeta = () => recompute();
      internal.addEventListener('loadedmetadata', onMeta);

      return () => internal.removeEventListener('loadedmetadata', onMeta);
    }
  }, [recompute]);

  if (isDefined(progress) && progress < 100) {
    return <Progress steps={10} size="small" percent={progress} status={progress === 100 ? 'success' : 'active'} />;
  }

  return (
    <>
      <div
        ref={containerRef}
        className="player-wrapper"
        style={{
          position: 'relative',
          aspectRatio: '16 / 9',
          maxWidth: 900,
          overflow: 'hidden',
        }}
      >
        <ReactPlayer
          className={styles.reactPlayer}
          url={videoUrl}
          playing={!videoPaused}
          controls
          onReady={handleReady}
          onPause={() => {
            recompute();
            setVideoPaused(true);
          }}
          onPlay={() => {
            recompute();
            setVideoPaused(false);
          }}
          width="100%"
          height="100%"
          style={{ objectFit: 'contain' }}
          ref={playerRef}
          config={{
            file: {
              attributes: {
                crossOrigin: 'anonymous',
              },
            },
          }}
        />

        {videoPaused && learnBtnActive ? (
          <div
            style={{
              position: 'absolute',
              left: contentBox.x,
              top: contentBox.y,
              width: contentBox.w,
              height: contentBox.h,
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            <Rnd
              size={{ width: crop.width, height: crop.height }}
              position={{ x: crop.x, y: crop.y }}
              ref={rndRef}
              bounds="parent"
              resizeHandleComponent={{
                topLeft: <ResizeDot />,
                topRight: <ResizeDot />,
                bottomLeft: <ResizeDot />,
                bottomRight: <ResizeDot />,
              }}
              dragHandleClassName="crop-drag-handle"
              minWidth={100}
              minHeight={100}
              onResize={(e, dir, ref, delta, pos) => {
                setCrop(() => {
                  const w = clamp(ref.offsetWidth, 10, contentBox.w);
                  const h = clamp(ref.offsetHeight, 10, contentBox.h);
                  const x = clamp(pos.x, 0, Math.max(0, contentBox.w - w));
                  const y = clamp(pos.y, 0, Math.max(0, contentBox.h - h));
                  return { x, y, width: w, height: h };
                });
              }}
              onDrag={(e, d) => {
                setCrop((c) => {
                  const x = clamp(d.x, 0, Math.max(0, contentBox.w - c.width));
                  const y = clamp(d.y, 0, Math.max(0, contentBox.h - c.height));
                  return { ...c, x, y };
                });
              }}
              style={{
                border: '2px solid rgba(239,68,68,.95)',
                backgroundColor: 'rgba(239,68,68,0.08)',
                borderRadius: 12,
                boxSizing: 'border-box',
              }}
            >
              <div style={{ pointerEvents: 'auto', width: '100%', height: '100%' }} className="crop-drag-handle" />
            </Rnd>
          </div>
        ) : null}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        {learnBtnActive ? (
          <div style={{ display: 'flex', gap: '1rem', paddingTop: '.5rem' }}>
            {screenShots.map((ss, idx) => {
              if (!ss.url)
                return (
                  <Button
                    onClick={() => handleCapture(idx)}
                    key={idx}
                    style={{
                      color: theme.colors.quoteBgc,
                      width: '100px',
                      height: '100px',
                      padding: '0px',
                      border: '1px dashed black',
                      background: 'transparent',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <AddIcon color={theme.colors.quoteBgc} />
                      <div style={{ marginTop: 8 }}>{t('nSearchPage.screenshotNumber', { number: idx + 1 })}</div>
                    </div>
                  </Button>
                );
              return (
                <Image
                  preview={{
                    mask: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <EyeOutlined style={{ fontSize: 20, color: 'white' }} />
                        <DeleteOutlined
                          style={{ fontSize: 20, color: 'white', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const ssList = [...screenShots];
                            ssList[idx] = { uid: idx.toString(), name: 'empty', url: undefined };
                            setScreenShots(ssList);
                          }}
                        />
                      </div>
                    ),
                  }}
                  key={idx}
                  height={100}
                  width={100}
                  src={ss.url}
                  style={{ objectFit: 'contain' }}
                  alt="Screenshot Preview"
                />
              );
            })}
          </div>
        ) : null}
        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
          {videoPaused && learnBtnActive ? (
            <Button
              style={{ background: theme.colors.quoteBgc }}
              onClick={() => {
                setLearnBtnActive(false);
                setScreenShots(DEFAULT_SCREENSHOTS);
              }}
            >
              {t('nSearchPage.cancelLearn')}
            </Button>
          ) : null}
          {screenShots.filter((obj) => obj.url).length === DEFAULT_SCREENSHOTS.length ? (
            <Button style={{ background: theme.colors.quoteBgc }} onClick={onAddPerson}>
              {t('nSearchPage.addPerson')}
            </Button>
          ) : null}
        </div>
      </div>
      {!learnBtnActive && !screenShots.filter((obj) => obj.url).length ? (
        <div style={{ paddingTop: '.5rem' }}>
          <Button
            style={{ background: theme.colors.quoteBgc, marginLeft: 'auto', display: 'block' }}
            onClick={() => {
              setLearnBtnActive(true);
              setVideoPaused(true);
            }}
          >
            {t('nSearchPage.learn')}
          </Button>
        </div>
      ) : null}
    </>
  );
};

const FenceCardStyle: CSSProperties = {
  borderRight: 'none',
  borderRadius: '0px',
  borderLeft: 'none',
  height: '100%',
};

const FenceInfoRow = ({ title, value }: { title: string; value: string | number }) => {
  return (
    <p className={styles.fenceRow}>
      {title}: <span className={styles.fenceRowValue}>{value}</span>
    </p>
  );
};

// This component is responsible for displaying fence info if the block has fence media type
const FenceInfo = ({ fenceData, themeBg }: { fenceData: FenceNode | undefined; themeBg: string }) => {
  if (!fenceData) return null;
  return (
    <Card
      title={fenceData.name}
      style={{ ...FenceCardStyle, borderBottom: `1px solid ${themeBg}` }}
      styles={{
        header: {
          borderRadius: '0px',
          borderBottom: `1px solid ${themeBg}`,
          borderTop: `1px solid ${themeBg}`,
          fontWeight: 800,
        },
      }}
    >
      <FenceInfoRow title="Status" value={getEnumKeyByValue(FenceSensorStatus, fenceData.status)} />
      <FenceInfoRow
        title="Temperature"
        value={`${isDefined(fenceData.temperature) ? fenceData.temperature : '--'}°C`}
      />
      <FenceInfoRow title="Humidity" value={`${isDefined(fenceData.humidity) ? fenceData.humidity : '--'}%`} />
      <FenceInfoRow title="Time" value={timeHelper.formatDate(fenceData.time)} />
      <FenceInfoRow title="Battery Status" value={getEnumKeyByValue(FenceBatteryStatus, fenceData.battery_status)} />
      <FenceInfoRow title="Power Source" value={getEnumKeyByValue(FencePowerSource, fenceData.power_source)} />
      <FenceInfoRow title="Technical Service Code" value={fenceData.technical_service_code} />
    </Card>
  );
};

// This component is responsible for displaying keypad info if the block has keypad media type
const KeypadInfo = ({ keypadData, themeBg }: { keypadData: Keypad | undefined; themeBg: string }) => {
  if (!keypadData) return null;
  return (
    <Card
      title={keypadData.keypad_name}
      style={{ ...FenceCardStyle, borderBottom: `1px solid ${themeBg}` }}
      styles={{
        header: {
          borderRadius: '0px',
          borderBottom: `1px solid ${themeBg}`,
          borderTop: `1px solid ${themeBg}`,
          fontWeight: 800,
        },
      }}
    >
      <FenceInfoRow title="Person" value={keypadData.person_name} />
      <FenceInfoRow title="Date" value={timeHelper.formatDate(keypadData.date)} />
      <FenceInfoRow title="Title" value={keypadData.title} />
    </Card>
  );
};

const BatPInfo = ({ batPData, themeBg }: { batPData: BatP | undefined; themeBg: string }) => {
  if (!batPData) return null;
  return (
    <Card
      title={batPData.batP_name}
      style={{ ...FenceCardStyle, borderBottom: `1px solid ${themeBg}` }}
      styles={{
        header: {
          borderRadius: '0px',
          borderBottom: `1px solid ${themeBg}`,
          borderTop: `1px solid ${themeBg}`,
          fontWeight: 800,
        },
      }}
    >
      <FenceInfoRow title="Person" value={batPData.person_name} />
      <FenceInfoRow title="Date" value={timeHelper.formatDate(batPData.date)} />
      <FenceInfoRow title="Title" value={batPData.title} />
    </Card>
  );
};

const SeNotInfo = ({ senotData, themeBg }: { senotData: SeNot | undefined; themeBg: string }) => {
  if (!senotData) return null;
  return (
    <Card
      title={senotData.title}
      style={{ ...FenceCardStyle, borderBottom: `1px solid ${themeBg}` }}
      styles={{
        header: {
          borderRadius: '0px',
          borderBottom: `1px solid ${themeBg}`,
          borderTop: `1px solid ${themeBg}`,
          fontWeight: 800,
        },
      }}
    >
      <FenceInfoRow title="Sensor" value={senotData.sensor_name} />
      <FenceInfoRow title="Sensor Type" value={senotData.sensor_type} />
      <FenceInfoRow title="Date" value={timeHelper.formatDate(senotData.date)} />
      <FenceInfoRow title="Event" value={senotData.event} />
      <FenceInfoRow title="Message" value={senotData.message} />
      <FenceInfoRow title="Note" value={senotData.note} />
    </Card>
  );
};

const BlockX = ({ data }: { data: string }) => {
  return <div style={{ color: 'var(--primary_text_color)' }}>{data}</div>;
};

const BlockDocument = ({
  blockId,
  filename,
  htmlContent,
  setHtmlContent,
}: {
  filename: string;
  blockId: string;
  htmlContent: string | undefined;
  setHtmlContent: React.Dispatch<React.SetStateAction<string | undefined>>;
}) => {
  const sanitizer = DOMPurify.sanitize;
  const { theme } = useAppSelector((state) => state.configContent);

  const { isCollapsed } = useContext(SidebarContext);

  const [blob, setBlob] = useState<Blob | undefined>(undefined);
  const [blobText, setBlobText] = useState<string>('');
  const [progress, setProgress] = useState<number>();
  const [pdfWidth, setPdfWidth] = useState<number>();

  const extension = filename ? filename.split('.').pop() : '';

  useEffect(() => {
    if (isCollapsed) setPdfWidth(700);
    else setPdfWidth(600);
  }, [isCollapsed]);

  const getHtmlContent = async (zipData: any) => {
    try {
      const zip = await JSZip.loadAsync(zipData);

      const fileMap: Record<string, string> = {};

      await Promise.all(
        Object.keys(zip.files).map(async (name) => {
          const file = zip.files[name];
          if (file.dir) return;

          if (/\.(png|jpg|jpeg|gif|webp)$/i.test(name)) {
            const content = await file.async('blob');
            const imgExtension = name ? name.split('.').pop() : '';
            const imgBlob = new Blob([content], { type: getMIMEType(imgExtension) });
            fileMap[name] = URL.createObjectURL(imgBlob);
          } else {
            const content = await file.async('string');
            fileMap[name] = content;
          }
        }),
      );
      const htmlFilename = Object.keys(fileMap).find((f) => f.toLowerCase().endsWith('.html'));
      if (!htmlFilename) return;
      let html = fileMap[htmlFilename];
      html = html.replace(/src="([^"]+)"/g, (match, path) => {
        if (fileMap[path]) return `src="${fileMap[path]}"`;
        return match;
      });
      setHtmlContent(html);
    } catch (error) {
      console.log('error', error);
    }
  };

  // Get blob url from decrypted url
  const getBlobUrl = async (url: string) => {
    try {
      const { data, headers } = await dfmAxios.get(
        `/${blockId}.dat`,

        {
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            const total = progressEvent.total || 1;
            const percentCompleted = Math.round((progressEvent.loaded / total) * 100);
            setProgress(percentCompleted);
          },
        },
      );
      const contentType = headers['content-type']?.toString() || 'application/octet-stream';

      // nsearch docx ve xlsx formatları zip formatında geliyor fakat
      // url uzantısı farklı olduğu için çakışma olmasını engellemek için  url.endsWith kontrolü eklendi
      if (contentType === 'application/zip' && url.endsWith('.zip')) {
        getHtmlContent(data);
        return;
      }

      if (TEXT_MIME_TYPES.includes(contentType)) {
        const text = await data.text();
        setBlobText(text);
      }
      setBlob(data);
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    getBlobUrl(filename);

    // TODO: PROGRESS Buradan Mı resetlenmeli?
    return () => {
      setBlob(undefined);
      setProgress(undefined);
    };
  }, [filename]);

  const pdfFile = useMemo(() => {
    if (!blob || extension !== 'pdf') return;
    return new File([blob], filename);
  }, [blob]);

  if (isDefined(progress) && progress < 100) {
    return <Progress steps={10} size="small" percent={progress} status={progress === 100 ? 'success' : 'active'} />;
  }
  if (blob) {
    if (!blob.size)
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Empty
            imageStyle={{ height: '100%' }}
            image={<NoDataIcon color={theme.colors.quoteBgc} style={{ width: '150px', height: '150px' }} />}
            description="Although the file is processed, the data cannot be accessed because it is corrupted."
          />
        </div>
      );
    return (
      <div>
        <span
          style={{
            color: 'var(--notification_content_color)',
            fontWeight: '700',
            borderBottom: blobText ? `1.5px solid ${theme.colors.quoteBgc}` : undefined,
            cursor: 'pointer',
          }}
          onClick={() => {
            if (!blob) return;

            fileDownload(blob, filename || `Document.${extension}`);
          }}
        >
          <Tooltip title="Click to download">{filename}</Tooltip>
        </span>
        <div className={styles.fullText}>{blobText}</div>

        {pdfFile ? (
          <div style={{ height: '50vh', overflow: 'auto' }}>
            <PDFViewer file={pdfFile} />
          </div>
        ) : null}
      </div>
    );
  }

  if (htmlContent) {
    return (
      <div
        style={{ color: 'var(--primary_text_color)', height: '50vh', overflow: 'auto' }}
        dangerouslySetInnerHTML={{
          __html: sanitizer(htmlContent, {
            ALLOWED_ATTR: ['class', 'id', 'src', 'href', 'style'],
            ALLOWED_URI_REGEXP: /^(?:(?:https?|blob):|data:image\/)/i,
            ALLOWED_TAGS: ['div', 'img', 'p', 'ul', 'li', 'a', 'hr', 'strong', 'figure', 'section'],
            FORCE_BODY: true,
            ADD_TAGS: ['style'],
          }),
        }}
      />
    );
    // return (
    //   <div>
    //     <a
    //       href={getFileContent.url}
    //       target="_blank"
    //       className={styles.newsUrl}
    //       style={{
    //         pointerEvents: isValidHttpUrl(getFileContent.url) ? undefined : 'none',
    //       }}
    //       rel="noreferrer"
    //     >
    //       <UrlIcon style={{ marginBottom: '-6px' }} />

    //       <span className={styles.urlText}>{getFileContent.url}</span>
    //     </a>
    //     {hasText ? <div className={styles.fullText}>{getFileContent?.full_text}</div> : null}
    //   </div>
    // );
  }

  // if (progress) {
  //   return <Progress steps={10} size="small" percent={progress} status={progress === 100 ? 'success' : 'active'} />;
  // }

  return null;
};

interface INewsInfoProp {
  getFileContent?: UrlInfoResultType;
  hasText?: boolean;
  blockData: BlockResultType;
}

const NewsInfo: React.FC<INewsInfoProp> = ({ getFileContent, blockData, hasText }) => {
  const { metadata, interestList } = blockData;
  const { customer } = useAppSelector((state) => state.user);
  const { theme } = useAppSelector((state) => state.configContent);

  const [showEmotionalAnalysis, setShowEmotionalAnalysis] = useState(false);
  const [selectedInterestNames, setSelectedInterestNames] = useState<DefaultOptionType[]>([]);
  const [blockEditModalOpen, setBlockEditModalOpen] = useState(false);
  const [blockCategories, setBlockCategories] = useState<BlockResultType['interestList']>();
  const [editBlockInterests, setEditBlockInterests] = useState<{
    [key: string]: EditBlockDataType[number];
  }>({});

  const { data: interestLookupList } = useGetAllInterestsQuery({ customerStatus: true });
  const { data: blockStatisticList } = useGetBlockStatisticsQuery({ blockId: metadata.id });
  const mainBlockStatistic = blockStatisticList?.rows.find((row) => !row.interestId);

  const [createBlockStatistic] = useCreateBlockStatisticMutation();
  const [updateBlockStatistic] = useUpdateBlockStatisticMutation();
  // const [deleteBlockStatistic] = useDeleteBlockStatisticMutation();
  const [editBlock] = useEditBlockMutation();

  const [htmlContent, setHtmlContent] = useState<string>();

  /*
    If sentiment analysis exists in dfm and selected status is not the same then update block statistic
    If sentiment analysis data does not exist in dfm result then create block statistic.
  */
  const onBlockStatisticChange = (dto: {
    status?: BlockStatisticStatus | null;
    relevantStatus?: BlockStatisticRelevantStatus | null;
  }) => {
    if (mainBlockStatistic) {
      updateBlockStatistic({ id: mainBlockStatistic.id, ...dto })
        .unwrap()
        .then(() => message.success('Block status changed successfully'));
    } else if (!mainBlockStatistic) {
      createBlockStatistic({ ...dto, blockId: metadata.id })
        .unwrap()
        .then(() => message.success('Block status changed successfully'));
    } else {
      // deleteBlockStatistic(mainBlockStatistic.id)
      //   .unwrap()
      //   .then(() => message.success('Block status changed successfully'));
    }
  };

  const filename = getFileContent?.file_name;

  const getContent = (blockResult: BlockResultType['metadata']) => {
    if (blockResult?.dataType === MediaType.Document) {
      if (getFileContent?.X) return <BlockX data={getFileContent.X} />;
      return (
        <BlockDocument
          blockId={metadata.id}
          filename={filename || ''}
          htmlContent={htmlContent}
          setHtmlContent={setHtmlContent}
        />
      );
    }
    if (blockResult.dataType === MediaType.Image) {
      return <BlockImage fileUrl={`${metadata.id}.dat`} text={getFileContent?.full_text} />;
    }
    if (blockResult.dataType === MediaType.Video) {
      return <BlockVideo file={`${metadata.id}.dat`} blockId={blockResult.id} />;
    }
    if (blockResult.dataType === MediaType.Fence_Data) {
      return (
        <div className={styles.fenceNode}>
          <div style={{ flex: '1' }}>
            <FenceInfo fenceData={getFileContent?.fence} themeBg={theme.colors.quoteBgc} />
          </div>
          <div style={{ flex: '1' }}>
            <BlockImage fileUrl={`${metadata.id}.dat`} />
          </div>
        </div>
      );
    }
    if (blockResult.dataType === MediaType.Keypad) {
      return <KeypadInfo keypadData={getFileContent?.keypad} themeBg={theme.colors.quoteBgc} />;
    }
    if (blockResult.dataType === MediaType.batP) {
      return <BatPInfo batPData={getFileContent?.batP} themeBg={theme.colors.quoteBgc} />;
    }
    if (blockResult.dataType === MediaType.SeNot) {
      return <SeNotInfo senotData={getFileContent?.senot} themeBg={theme.colors.quoteBgc} />;
    }
  };

  const showShareButton = genericExcludeFilter([BRONZE_ACCOUNT_ID], customer?.customerAccountTypeId);
  const onSaveBlockEdit = () => {
    const addedInterests: any = [];

    selectedInterestNames.forEach((interest) =>
      interestLookupList?.rows?.forEach((list) =>
        list.interests.forEach((int) => {
          if (int.id === interest.value) {
            addedInterests.push({
              id: int.id,
              interestClassType: int.classType,
              type: int.type,
            });
          }
        }),
      ),
    );

    const editedInterests = Object.values(editBlockInterests);
    editBlock({ blockId: metadata.id, interests: [...editedInterests, ...addedInterests] })
      .unwrap()
      .then(() => {
        message.success('Block is updated successfully');
        setBlockEditModalOpen(false);
      })
      .catch(() => message.error('Block can not be updated.'));
  };

  const onClickHtmlPdf = async (file: any, showed: boolean) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = file;
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '0';
    tempDiv.style.width = '750px';

    document.body.appendChild(tempDiv);

    const images = tempDiv.querySelectorAll('img');
    images.forEach((img) => {
      if (img.hasAttribute('srcset')) {
        img.removeAttribute('srcset');
      }
    });

    const imagePromises = Array.from(images).map((img) => {
      if (img.complete) return Promise.resolve();

      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
    });

    Promise.all(imagePromises);

    const pdfSettings = {
      margin: [4, 4, 8, 4] as [number, number, number, number],
      filename: `${filename?.split('.')[0]}.pdf`,
      image: {
        type: 'jpeg' as const,
        quality: 0.98,
      },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        windowWidth: 830,
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait' as const,
      },
      pagebreak: {
        mode: ['css', 'legacy'],
        avoid: ['img', 'strong', 'ul', 'ol', 'tr', 'h1', 'h2', 'table', 'section', 'p', 'a', 'div'],
      },
    };

    const pdfBlob = await html2pdf().set(pdfSettings).from(tempDiv).output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    if (showed) {
      showModal({
        title: `${filename}`,
        content: <PDFViewer file={blobUrl} />,
        size: 'xlarge-2',
        backgroundColor: 'white',
        footer: [],
      });
    } else {
      fileDownload(pdfBlob, `${filename?.split('.')[0]}.pdf`);
    }

    document.body.removeChild(tempDiv);
  };

  const bottomContent = useMemo(() => {
    const contents: ReactNode[] = [];

    if (getFileContent?.emotional_analysis?.length) {
      contents.push(
        <Tooltip title="Emotional Analysis">
          <div>
            <FaChartBar
              style={{ cursor: 'pointer', marginTop: '0.5rem' }}
              onClick={() => {
                setShowEmotionalAnalysis((prev) => !prev);
              }}
              color="#1a3441"
              size={30}
            />
          </div>
        </Tooltip>,
      );
    }

    // if (getFileContent?.url && showShareButton) {
    //   contents.push(
    //     <div className={styles.statusButtonsWrapper}>
    //       <Menu onClick={() => {}} mode="horizontal" style={{ lineHeight: '0' }}>
    //         <SubMenu
    //           style={{ background: 'var(--page_content_bg)' }}
    //           icon={<ShareIcon />}
    //           popupClassName={styles.subMenu}
    //           popupOffset={[-27, 15]}
    //         >
    //           <MenuItem style={{ justifyItems: 'center', background: '#1A3441' }}>
    //             <FacebookShareButton url={getFileContent.url}>
    //               <FacebookIcon />
    //             </FacebookShareButton>
    //           </MenuItem>
    //           <div
    //             style={{
    //               borderLeft: '0.5px solid #c66d49',
    //               height: '30px',
    //               marginTop: '8px',
    //             }}
    //           />
    //           <MenuItem style={{ justifyItems: 'center', background: '#1A3441' }}>
    //             <LinkedinShareButton url={getFileContent.url}>
    //               <LinkedinIcon />
    //             </LinkedinShareButton>
    //           </MenuItem>
    //         </SubMenu>
    //       </Menu>
    //     </div>,
    //   );
    // }

    if (getFileContent?.sentiment_analysis || mainBlockStatistic?.status) {
      contents.push(
        <>
          {mainBlockStatistic?.status === BlockStatisticStatus.NEGATIVE ? (
            <Tooltip title="Negative">
              <BiSolidDislike
                onClick={() => onBlockStatisticChange({ status: null })}
                size={22}
                color="black"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Negative">
              <BiDislike
                onClick={() => onBlockStatisticChange({ status: BlockStatisticStatus.NEGATIVE })}
                size={22}
                color="black"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          )}
          {mainBlockStatistic?.status === BlockStatisticStatus.NEUTRAL ? (
            <Tooltip title="Neutral">
              <AiFillMinusCircle
                onClick={() => onBlockStatisticChange({ status: null })}
                size={22}
                color="black"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Neutral">
              <AiOutlineMinusCircle
                onClick={() => onBlockStatisticChange({ status: BlockStatisticStatus.NEUTRAL })}
                size={22}
                color="black"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          )}
          {mainBlockStatistic?.status === BlockStatisticStatus.POSITIVE ? (
            <Tooltip title="Positive">
              <AiFillLike
                onClick={() => onBlockStatisticChange({ status: null })}
                size={22}
                color="black"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Positive">
              <AiOutlineLike
                onClick={() => onBlockStatisticChange({ status: BlockStatisticStatus.POSITIVE })}
                size={22}
                color="black"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          )}
        </>,
      );
    }

    if (metadata.dataType === MediaType.Document) {
      contents.push(
        <div style={{ display: 'flex', columnGap: '.5rem' }}>
          {mainBlockStatistic?.relevantStatus === BlockStatisticRelevantStatus.IRRELEVANT ? (
            <Tooltip title="Irrelevant">
              <MdCancel
                onClick={() => onBlockStatisticChange({ relevantStatus: null })}
                size={22}
                color="var(--primary_text_color)"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Irrelevant">
              <MdOutlineCancel
                onClick={() => onBlockStatisticChange({ relevantStatus: BlockStatisticRelevantStatus.IRRELEVANT })}
                size={22}
                color="var(--primary_text_color)"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          )}
          {mainBlockStatistic?.relevantStatus === BlockStatisticRelevantStatus.RELEVANT ? (
            <Tooltip title="Relevant">
              <MdCheckCircle
                onClick={() => onBlockStatisticChange({ relevantStatus: null })}
                size={22}
                color="var(--primary_text_color)"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Relevant">
              <MdCheckCircleOutline
                onClick={() => onBlockStatisticChange({ relevantStatus: BlockStatisticRelevantStatus.RELEVANT })}
                size={22}
                color="var(--primary_text_color)"
                style={{ cursor: 'pointer' }}
              />
            </Tooltip>
          )}
        </div>,
      );
    }

    // contents.push(
    //   <Tooltip title="Edit Block">
    //     <div>
    //       <EditIcon
    //         color="var(--primary_text_color)"
    //         onClick={() => {
    //           const categories = convertInterestListToCategories(interestList);
    //           setBlockCategories(categories);
    //           setBlockEditModalOpen(true);
    //         }}
    //       />
    //     </div>
    //   </Tooltip>,
    // );

    if (htmlContent) {
      contents.push(
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            columnGap: '1rem',
            justifyContent: 'flex-start',
            paddingTop: '0.3rem',
          }}
        >
          <Tooltip title="View">
            <div>
              <FiEye
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  onClickHtmlPdf(htmlContent, true);
                }}
                color="#1a3441"
                size={24}
              />
            </div>
          </Tooltip>
          <Tooltip title="Download">
            <div>
              <PiDownloadSimple
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  onClickHtmlPdf(htmlContent, false);
                }}
                color="#1a3441"
                size={24}
              />
            </div>
          </Tooltip>
        </div>,
      );
    }
    return contents;
  }, [getFileContent, metadata, mainBlockStatistic, htmlContent]);

  const conf = mediaTypeConf(metadata.dataType);
  const MediaTypeIcon = conf.icon;

  if (!metadata || !getFileContent) return null;

  return (
    <>
      <>
        <div className={styles.newsInfoContainer}>
          <div className={styles.titleContainer}>
            <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
              <MediaTypeIcon color={conf.pill.color} fontSize={35} />
              <div style={{ display: 'flex', flexDirection: 'column', rowGap: '.5rem' }}>
                <h1 className={styles.newsTitle}>
                  <EllipsisTooltip type="vertical">{getFileContent?.title}</EllipsisTooltip>
                </h1>
                <div className={styles.pillContainer}>
                  <div
                    className={styles.pill}
                    style={{
                      color: conf.pill.color,
                      border: `1px solid ${conf.pill.borderColor}`,
                    }}
                  >
                    {getEnumKeyByValue(MediaType, metadata.dataType)}
                  </div>
                  {/* <span>1.8 MB</span> */}

                  <div style={{ display: 'flex', gap: '.3rem', alignItems: 'center' }}>
                    <div
                      style={{
                        width: '6px',
                        height: '6px',
                        background: 'var(--primary_text_color)',
                        borderRadius: '50%',
                      }}
                    />
                    <span>{timeHelper.formatDate(metadata.startTime)}</span>
                  </div>
                </div>
              </div>
            </div>
            <Tooltip title="Edit Block">
              <div style={{ paddingRight: '1rem' }}>
                <EditIcon
                  color="var(--primary_text_color)"
                  onClick={() => {
                    const categories = convertInterestListToCategories(interestList);
                    setBlockCategories(categories);
                    setBlockEditModalOpen(true);
                  }}
                />
              </div>
            </Tooltip>
          </div>
          <div style={{ paddingTop: '1rem' }}>{getContent(metadata)}</div>
        </div>

        {bottomContent.length ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '1rem',
              justifyContent: 'flex-end',
              paddingTop: '1rem',
            }}
          >
            {bottomContent.map((rn) => rn)}
          </div>
        ) : null}

        {showEmotionalAnalysis ? (
          <DraggableModal
            disableResize
            style={{ padding: '0px' }}
            title="Emotional Analysis"
            onClose={() => setShowEmotionalAnalysis(false)}
            contentDraggable
            size={{
              width: 500,
              height: 400,
            }}
            containerStyle={{ top: '50%', left: '50%' }}
          >
            <div style={{ width: '90%', marginInlineStart: '10%' }}>
              <Pie
                legend={{
                  position: document.body.dir === 'rtl' ? 'left' : 'right',
                  itemName: {
                    formatter: (value) => value.charAt(0).toUpperCase() + value.slice(1),
                    style: {
                      textAlign: 'left',
                    },
                  },
                }}
                data={getFileContent.emotional_analysis || []}
                angleField="score"
                colorField="label"
                tooltip={{
                  customContent: (title, items) => {
                    return (
                      <div
                        style={{
                          padding: '10px',
                          backgroundColor: '#fff',
                          borderRadius: '4px',
                          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        {items.map((item) => (
                          <div key={item.name} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: item.color,
                                marginRight: '8px',
                              }}
                            />
                            <strong style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                              {item.name.charAt(0).toUpperCase() + item.name.slice(1)}
                            </strong>
                            : <span style={{ marginLeft: '5px' }}>{item.value}</span>
                          </div>
                        ))}
                      </div>
                    );
                  },
                }}
              />
            </div>
          </DraggableModal>
        ) : null}

        {blockEditModalOpen ? (
          <DraggableModal
            footer={
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '1rem' }}>
                <Button
                  style={{ background: theme.colors.quoteBgc }}
                  onClick={onSaveBlockEdit}
                  className={styles.saveBtn}
                >
                  Save
                </Button>
              </div>
            }
            disableResize
            title="Edit Block"
            onClose={() => setBlockEditModalOpen(false)}
            size={{ width: 500, height: 500 }}
            // resizeOpts={{ minConstraints: [500, 500] }}
          >
            {blockCategories ? (
              <BlockStatisticList
                blockInterests={interestList}
                selectedInterestNames={selectedInterestNames}
                setSelectedInterestNames={setSelectedInterestNames}
                blockCategories={blockCategories}
                blockId={metadata.id}
                setEditBlockInterests={setEditBlockInterests}
                editBlockInterests={editBlockInterests}
              />
            ) : null}
          </DraggableModal>
        ) : null}
      </>

      <div />
    </>
  );
};

NewsInfo.defaultProps = {
  hasText: true,
};

export default NewsInfo;
